import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Resets scroll to top whenever the route path changes.
 *
 * Strategy:
 *   1. Honour explicit hash anchors (#section) — scroll to that element.
 *   2. Otherwise scroll the page to (0, 0).
 *   3. Also reset internal scroll containers that opt in via the
 *      `data-scroll-container="true"` attribute.
 *
 * Mount this exactly once inside <BrowserRouter>, before <Routes>.
 */
export default function ScrollToTop() {
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    // If route includes a hash anchor, jump to it
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "auto", block: "start" });
        return;
      }
    }
    // Standard reset
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch {
      window.scrollTo(0, 0);
    }
    // Reset any opt-in internal containers
    document.querySelectorAll('[data-scroll-container="true"]').forEach((el) => {
      try { el.scrollTop = 0; } catch { /* ignore */ }
    });
    // Optional: bring the main content into focus for screen-readers
    const main = document.querySelector('[data-main-content="true"]');
    if (main && typeof main.focus === "function") {
      try { main.focus({ preventScroll: true }); } catch { /* ignore */ }
    }
  }, [pathname, search, hash]);

  return null;
}
